import { useCallback } from "react";
import { useAppDispatch } from "../hooks";
import { uploadImagesAction } from "./ImageActions";

/**
 * Shared block/multi-image upload primitive (SCRUM-1021) — the multi-image
 * companion to {@link import("./useImageUpload").useImageUpload}.
 *
 * `uploadImagesAction` already resolves to the uploaded (secure) URL, so a block
 * editor can upload-then-set-inline as a plain awaited sequence instead of the
 * legacy `state.images.uploadedImages` watch-effect that merged the URL back by
 * index. That effect was racy: the slice accumulated across uploads and was
 * matched by `index`, so a stale entry could land on the wrong block.
 *
 *   const url = await uploadBlockImage(block.index, block.image.data, path);
 *   return { ...block, image: url };
 *
 * Contract:
 * - Returns the uploaded URL for one block image; the caller sets it on the
 *   block and owns the save + error toast.
 * - Uses `uploadImagesAction` (the `images.uploadedImages` slice), NOT
 *   `uploadImageAction`, so concurrent block uploads inside a `Promise.all` do
 *   not race on the single `images.url` value that the media browsers watch.
 * - Errors propagate: `uploadImagesAction` re-throws on a failed upload
 *   (SCRUM-815), so the caller's per-block try/catch surfaces it.
 *
 * NOTE: `uploadImagesAction` still dispatches `QUOTES_RECEIVED` into the
 * now-unread `images.uploadedImages` slice. Once the blog containers stop reading
 * that slice (this change), the slice + its reducer case are dead and can be
 * retired in a small follow-up cleanup (blog-only; see docs/Client_Image_Uploads.md).
 *
 * Returns `uploadBlockImage`.
 */
export function useBlockImageUpload(): (
  index: number,
  data: string,
  path: string
) => Promise<string> {
  const dispatch = useAppDispatch();
  return useCallback(
    (index: number, data: string, path: string) =>
      dispatch(uploadImagesAction(index, data, path)),
    [dispatch]
  );
}

